import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

import CollapsibleSection from '../../common/collapsibleSection';
import { INTERNATIONAL_ZIP_CODE, PNET_APP_QUERY_PARAM, shippingMethods, shippingPriorities } from '../../common/partsNetConstants';
import AccountNumberAutoSuggests from '../common/accountNumberAutoSuggestsAvailPricing';
import NewShipToAddressOverlayDialog from '../../dialogs/newShipToAddressOverlayDialog';
import QuantityInput from '../common/quantityInput';
import {
	getAccountDetails,
	getAddressAttributes,
	getDeliveryPhone,
	getNCRPSalesAgreementForMarketAreaAndRegion,
	getOnlinePricingForParts,
	getSelectedAccountsPriceSource,
	personShipTo,
	processPartsOrder,
	saveAddressList,
	saveAvailabilityOnChangeAddress,
	saveAvailabilityPricingOfImportedFile,
	saveCreateOrderPayload,
	saveGenericNCRPAccount,
	saveSelectedAddressBackup,
	updateSelectedAddress
} from '../../../redux/actions';
import { routes } from '../../../redux/api/routes';
import useDebounce from '../../../hooks/useDebounce';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { browserHistory } from 'react-router';
import GenericOverlayDialog from '../../dialogs/genericOverlayDialog';
import { ACCOUNT_NUMBER_TO_CUSTOMER } from '../../common/ncrpConstants';
import AddressSearchDialog from '../../dialogs/addressSearchDialog';

const { ncrpData } = routes;

// @param orderType - R or E
const getShippingMethods = orderType => {
	return shippingMethods['domestic'][orderType === 'R' ? 'regular' : 'emergency'];
};

const getInitialItems = () =>
	[...Array(6)].map(() => ({
		id: uuidv4(),
		itemNumber: '',
		requestedQuantity: '',
		accountNumber: ncrpData.accountNumber,
		itemDetails: null,
		agreementNumber: null,
		requestedQuantityError: false,
		agreementNumberError: false,
		customerReference: '',
		customerReferenceError: false
	}));

const DISABLED_ADDRESS_FIELDS = { country: true, addressLine1: true, city: true, state: true, province: true, zipCode: true, postalCode: true };

const CustomerReferenceInput = ({ value, onChange }) => {
	const [customerReference, setCustomerReference] = useState(value.toUpperCase());
	const debouncedCustomerReference = useDebounce(customerReference);

	useUpdateEffect(() => {
		onChange(debouncedCustomerReference);
	}, [debouncedCustomerReference]);

	const handleChange = event => {
		setCustomerReference(event.target.value.toUpperCase());
	};

	return <input type="text" className="fullWidth" value={customerReference} onChange={handleChange} />;
};

const NCRPOrderForm = props => {
	const { user, order } = useSelector(({ user, order }) => ({ user, order }));
	const [items, setItems] = useState(getInitialItems);
	const [salesAgreement, setSalesAgreement] = useState();
	const [fields, setFields] = useState(() => {
		const {
			selectedAccount: { ACCOUNTNUMBER: accountNumber }
		} = user;
		const orderType = 'R';

		return {
			shipTo: {},
			orderType,
			shippingMethod: getShippingMethods(orderType)[0].displayName,
			emailId: '',
			emailConfirmationToInputter: false,
			phoneNumber: '',
			customerPONumber: `${accountNumber}-NCRP`,

			customerInstructions: '',
			shippingInstructions: 'NOCHARGE'
		};
	});
	const [errors, setErrors] = useState({
		emailId: false,
		emailConfirmationToInputter: false,
		phoneNumber: false,

		customerInstructions: false,
		shippingInstructions: false
	});
	const [dialog, setDialog] = useState();
	const [errorMessage, setErrorMessage] = useState(null);

	const changeMethodRef = useRef([]);
	const siteToSalesAgreementNumberRef = useRef({});
	const phoneNumberRef = useRef({});
	const dispatch = useDispatch();

	const hasPricingAccess = props.isResponsibilityExists('Price');
	const hasCreateOrderAccess = props.isResponsibilityExists('Create New Order');

	const genericNCRPAccountSalesAgreements = (user && user.genericNCRPAccount && user.genericNCRPAccount.salesAgreements) || [];

	useEffect(() => {
		const genericNCRPAccountData = {
			accountDetails: user && user.genericNCRPAccount && user.genericNCRPAccount.accountDetails,
			salesAgreements: user && user.genericNCRPAccount && user.genericNCRPAccount.salesAgreements
		};

		const callbackAfterGenericNCRPAccountDetails = () => {
			if (genericNCRPAccountData.accountDetails && genericNCRPAccountData.salesAgreements) {
				dispatch(saveGenericNCRPAccount(genericNCRPAccountData));
			}
		};

		const fetchGenericNCRPAccountDetails = () => {
			dispatch(
				getAccountDetails(`accountNumber=${ncrpData.accountNumber}&requestType=getAccountDetails`, true, accountDetailsResponse => {
					if (accountDetailsResponse.status && accountDetailsResponse.responseObject && accountDetailsResponse.responseObject.length > 0) {
						genericNCRPAccountData.accountDetails = accountDetailsResponse.responseObject[0];
						callbackAfterGenericNCRPAccountDetails();
					}
				})
			);
		};

		if (!genericNCRPAccountData.accountDetails) {
			fetchGenericNCRPAccountDetails();
		}

		const fetchGenericNCRPAccountSalesAgreements = () => {
			dispatch(
				getSelectedAccountsPriceSource(`accountNumber=${ncrpData.accountNumber}&requestType=getSalesAgreementList`, salesAgreementListResponse => {
					genericNCRPAccountData.salesAgreements =
						salesAgreementListResponse.responseObject && salesAgreementListResponse.responseObject.filter(salesAgreement => salesAgreement.STATUS === 'ACTIVE');
					callbackAfterGenericNCRPAccountDetails();
				})
			);
		};

		if (!genericNCRPAccountData.salesAgreements) {
			fetchGenericNCRPAccountSalesAgreements();
		}

		return () => {
			const addressList = JSON.parse(JSON.stringify(user.addressList));
			addressList.responseObject = addressList.responseObject.filter(address => address.LOCATION !== 'NEW_SHIP_TO_NCRP');
			dispatch(saveAddressList(addressList));
			props.resetShipToAddress();
		};
	}, []);

	useEffect(() => {
		if (user.selectedAddress) {
			const selectedAddress = JSON.parse(user.selectedAddress);
			setFields(fields => ({
				...fields,
				shipTo: selectedAddress.LOCATION === 'NEW_SHIP_TO_NCRP' ? selectedAddress : { ...selectedAddress, NAME: user.selectedAccount.ORGANIZATIONNAME }
			}));

			const getDeliveryPhoneBySiteUseId = (siteUseId, callback) => {
				dispatch(
					getDeliveryPhone(
						`requestType=getDeliveryPhone&accountNumber=${user.selectedAccount.ACCOUNTNUMBER}${siteUseId ? `&siteUseId=${siteUseId}` : ''}`,
						deliveryPhoneResponse => {
							if (
								deliveryPhoneResponse.status &&
								deliveryPhoneResponse.responseObject &&
								deliveryPhoneResponse.responseObject.find(itm => itm.CONTACT_POINT_PURPOSE === 'CALL BEFORE DELIVERY' && itm.CONTACT_POINT_TYPE === 'PHONE')
							) {
								const phoneNumber = deliveryPhoneResponse.responseObject.find(
									itm => itm.CONTACT_POINT_PURPOSE === 'CALL BEFORE DELIVERY' && itm.CONTACT_POINT_TYPE === 'PHONE'
								).PHONE_NUMBER;
								callback(phoneNumber);
							} else {
								callback('');
							}
						}
					)
				);
			};

			const updatePhoneNumber = (phoneNumber, address) => {
				setFields(fields => ({
					...fields,
					phoneNumber: props.normalizePhoneNumber(phoneNumber, !['US', 'CA'].includes(address && address.COUNTRY))
				}));
			};

			const populatePhoneNumber = () => {
				const soldToAddress = props.getAddressByCategory('SOLD_TO', false);

				if (phoneNumberRef.current[selectedAddress.SITE_USE_ID] !== undefined) {
					if (phoneNumberRef.current[selectedAddress.SITE_USE_ID]) {
						updatePhoneNumber(phoneNumberRef.current[selectedAddress.SITE_USE_ID], selectedAddress);
					} else if (soldToAddress && phoneNumberRef.current[soldToAddress.SITE_USE_ID]) {
						updatePhoneNumber(phoneNumberRef.current[soldToAddress.SITE_USE_ID], soldToAddress);
					} else {
						updatePhoneNumber(phoneNumberRef.current.default, soldToAddress);
					}
				} else {
					getDeliveryPhoneBySiteUseId(selectedAddress.SITE_USE_ID, phoneNumber => {
						phoneNumberRef.current[selectedAddress.SITE_USE_ID] = phoneNumber;
						if (!phoneNumber) {
							if (soldToAddress) {
								if (phoneNumberRef.current[soldToAddress.SITE_USE_ID] !== undefined) {
									if (phoneNumberRef.current[soldToAddress.SITE_USE_ID]) {
										updatePhoneNumber(phoneNumberRef.current[soldToAddress.SITE_USE_ID], soldToAddress);
									} else {
										updatePhoneNumber(phoneNumberRef.current.default, soldToAddress);
									}
									updatePhoneNumber(phoneNumberRef.current[soldToAddress.SITE_USE_ID], soldToAddress);
								} else {
									getDeliveryPhoneBySiteUseId(soldToAddress.SITE_USE_ID, phoneNumber => {
										phoneNumberRef.current[soldToAddress.SITE_USE_ID] = phoneNumber;

										if (!phoneNumber) {
											if (phoneNumberRef.current.default !== undefined) {
												updatePhoneNumber(phoneNumberRef.current.default, soldToAddress);
											} else {
												getDeliveryPhoneBySiteUseId(null, phoneNumber => {
													phoneNumberRef.current.default = phoneNumber;
													updatePhoneNumber(phoneNumber, soldToAddress);
												});
											}
										} else {
											updatePhoneNumber(phoneNumber, soldToAddress);
										}
									});
								}
							}
						} else {
							updatePhoneNumber(phoneNumber, selectedAddress);
						}
					});
				}
			};

			populatePhoneNumber();

			const refreshAvailability = ({ isItemOrderabilityRequired, zipCode } = {}) => {
				const newItems = JSON.parse(JSON.stringify(items));

				const validItems = [];

				newItems.forEach(item => {
					if (
						!item.isInvalid &&
						!item.isNLA &&
						// !item.isUnsourced &&
						item.itemDetails &&
						// item.itemDetails.pricingData &&
						// item.itemDetails.pricingData.records &&
						// item.itemDetails.pricingData.records.length &&
						// (!this.state.isPOKEnabled || !item.isPOKInstall) &&
						(isItemOrderabilityRequired || !item.isRestricted)
					) {
						validItems.push({
							id: item.id,
							itemNumber: item.itemNumber,
							requestedQuantity: item.requestedQuantity,
							agreementNumber: item.agreementNumber,
							itemType: item.itemType
						});
						item.isAddressChanged = true;
					}
				});
				if (validItems.length) {
					props.getItemDetails(
						{ items: validItems },
						data => {
							const availabilityOnChangeAddress = [];

							newItems.forEach(item => {
								if (item.isAddressChanged) {
									const foundItem = data.items.find(updatedItem => updatedItem.id === item.id && updatedItem.itemNumber === item.itemNumber);
									availabilityOnChangeAddress.push({
										id: item.id,
										sku: foundItem.itemNumber,
										availabilityData: foundItem.availabilityData ? [foundItem.availabilityData] : []
									});
									item.isRestricted = foundItem.isRestricted;
								}
							});

							dispatch(saveAvailabilityOnChangeAddress(availabilityOnChangeAddress));
							setItems(newItems);
						},
						{
							searchContext: 'NCRP',
							include: { availability: true, orderability: true },
							showLoader: true
						}
					);
				}
			};

			refreshAvailability({
				isItemOrderabilityRequired: true,
				zipCode: selectedAddress.COUNTRY === 'US' ? selectedAddress.POSTALCODE && selectedAddress.POSTALCODE.split('-')[0] : INTERNATIONAL_ZIP_CODE
			});
		}
	}, [user.selectedAddress]);

	useEffect(() => {
		const { selectedAccount, selectedAddress: selectedAddressString } = user;
		const selectedAddress = JSON.parse(selectedAddressString || '{}');
		const selectedAccountType = props.getSelectedAccountType();

		const updateSalesAgreement = salesAgreementNumber => {
			let salesAgreement =
				salesAgreementNumber &&
				genericNCRPAccountSalesAgreements.find(
					genericAccountSalesAgreement => +genericAccountSalesAgreement.AGREEMENT_NUMBER === +salesAgreementNumber && genericAccountSalesAgreement.STATUS === 'ACTIVE'
				);

			if (salesAgreementNumber && !salesAgreement) {
				browserHistory.push('/error');
				return;
			}

			if (!salesAgreement) {
				salesAgreement = genericNCRPAccountSalesAgreements.find(
					genericAccountSalesAgreement => genericAccountSalesAgreement.AGREEMENT_NAME.toUpperCase().includes(' HQ ') && genericAccountSalesAgreement.STATUS === 'ACTIVE'
				);
			}

			// console.log('SALES AGREEMENT: ', salesAgreement.AGREEMENT_NAME, salesAgreement.AGREEMENT_NUMBER);
			setSalesAgreement(salesAgreement);
			setItems(items => items.map(item => ({ ...item, agreementNumber: salesAgreement.AGREEMENT_NUMBER })));
		};

		const findAndUpdateSalesAgreement = ({ region, marketArea }, callback) => {
			dispatch(
				getNCRPSalesAgreementForMarketAreaAndRegion(
					`requestType=getSalesAgreement&region=${region}${marketArea ? `&marketArea=${marketArea}` : ''}&appName=NCRP`,
					true,
					salesAgreementResponse => {
						if (salesAgreementResponse.responseObject) {
							updateSalesAgreement(salesAgreementResponse.responseObject.salesAgreementNo);
							callback && callback(salesAgreementResponse.responseObject.salesAgreementNo);
						} else {
							updateSalesAgreement(null);
							callback && callback(null);
						}
					}
				)
			);
		};

		if (selectedAccountType === 'HQ' || ACCOUNT_NUMBER_TO_CUSTOMER[selectedAccount.ACCOUNTNUMBER]) {
			if (!salesAgreement && genericNCRPAccountSalesAgreements.length) {
				const salesAgreementKeyword = ` ${selectedAccountType === 'HQ' ? selectedAccountType : ACCOUNT_NUMBER_TO_CUSTOMER[selectedAccount.ACCOUNTNUMBER]} `.toUpperCase();

				let salesAgreementForCustomer = genericNCRPAccountSalesAgreements.find(
					salesAgreement => salesAgreement.AGREEMENT_NAME.toUpperCase().includes(salesAgreementKeyword) && salesAgreement.STATUS === 'ACTIVE'
				);
				// console.log('Customer: ', salesAgreementKeyword);
				// console.log('SALES AGREEMENT: ', salesAgreementForCustomer.AGREEMENT_NAME, salesAgreementForCustomer.AGREEMENT_NUMBER);
				setSalesAgreement(salesAgreementForCustomer);
				setItems(items => items.map(item => ({ ...item, agreementNumber: salesAgreementForCustomer.AGREEMENT_NUMBER })));
			}
		} else if (selectedAddress && genericNCRPAccountSalesAgreements.length) {
			let address;

			if (selectedAddress.LOCATION === 'NEW_SHIP_TO_NCRP') {
				address = props.getCombinedAddresses().filter(itm => itm.PURPOSE === 'SOLD_TO')[0];
			} else {
				address = selectedAddress;
			}

			if (address.COUNTRY === 'CA') {
				if (siteToSalesAgreementNumberRef.current['REGION_CANADA']) {
					updateSalesAgreement(siteToSalesAgreementNumberRef.current['REGION_CANADA']);
				} else {
					findAndUpdateSalesAgreement({ region: 'Canada' }, salesAgreementNumber => {
						if (salesAgreementNumber) {
							siteToSalesAgreementNumberRef.current['REGION_CANADA'] = salesAgreementNumber;
						}
					});
				}
			} else {
				if (address.PARTYSITEID) {
					if (siteToSalesAgreementNumberRef.current[address.PARTYSITEID]) {
						updateSalesAgreement(siteToSalesAgreementNumberRef.current[address.PARTYSITEID]);
					} else {
						dispatch(
							getAddressAttributes(`siteNumber=${address.PARTYSITEID}&requestType=getAddressAttributes`, true, addressAttributesResponse => {
								if (addressAttributesResponse.responseObject && addressAttributesResponse.responseObject.length) {
									const regionAttribute = addressAttributesResponse.responseObject.find(attribute => attribute.ATTR_NAME === 'Region');
									const region = regionAttribute && regionAttribute.ATTR_VAL;
									const marketAreaAttribute = addressAttributesResponse.responseObject.find(attribute => attribute.ATTR_NAME === 'Market-Area');
									const marketArea = marketAreaAttribute && marketAreaAttribute.ATTR_VAL;

									// console.log(`Region - ${region}, Market Area - ${marketArea}`);

									findAndUpdateSalesAgreement({ region, marketArea }, salesAgreementNumber => {
										if (salesAgreementNumber) {
											siteToSalesAgreementNumberRef.current[address.PARTYSITEID] = salesAgreementNumber;
										}
									});
								}
							})
						);
					}
				}
			}
		}
	}, [user.selectedAddress, genericNCRPAccountSalesAgreements]);

	const handleAddressSelection = address => {
		if (!user.selectedAddressBackup || user.selectedAddressBackup.SITE_USE_ID !== address.SITE_USE_ID) {
			dispatch(saveSelectedAddressBackup(address));
		}
		const zipCode = address.POSTALCODE && address.POSTALCODE.split('-')[0];
		localStorage.setItem('zipCodeChanged', false);
		dispatch(updateSelectedAddress(JSON.stringify(address)));
		props.saveCustomZipCode(zipCode);
		props.setDefaultADC(address, user.selectedAccount.ACCOUNTNUMBER);
		setDialog(null);
	};

	const handleRemoveLine = (itemIndex, e) => {
		e.preventDefault();

		setItems(items =>
			items.filter((_item, index) => {
				return index !== itemIndex;
			})
		);

		changeMethodRef.current = changeMethodRef.current.filter((_method, index) => index !== itemIndex);
	};

	const handleRemoveAll = e => {
		e.preventDefault();

		setItems(getInitialItems(salesAgreement.AGREEMENT_NUMBER));
		changeMethodRef.current = [];
	};

	const handleMoreLines = () => {
		const agreementNumber = salesAgreement.AGREEMENT_NUMBER;
		const newItems = Array.from({ length: 10 }, () => ({
			id: uuidv4(),
			itemNumber: '',
			requestedQuantity: '',
			itemDetails: null,
			agreementNumber,
			requestedQuantityError: false,
			agreementNumberError: false,
			customerReference: '',
			customerReferenceError: false
		}));

		setItems(items => [...items, ...newItems]);
	};

	const isValidPhoneNumber = phoneNumber => {
		if (['US', 'CA'].includes(fields.shipTo.COUNTRY)) {
			return /^1?[2-9][0-9]{9}$/.test(phoneNumber.replace(/\D+/g, ''));
		}
		return /^\+?[0-9]{1,20}$/.test(phoneNumber.trim());
	};

	const isEmailIdValid = emailId => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}$/.test(emailId);

	const isFieldValid = (fieldName, fieldValue) => {
		if (fieldName === 'phoneNumber') {
			if (fieldValue.trim() === '' || !isValidPhoneNumber(fieldValue)) {
				return false;
			}
		} else if (fieldName === 'emailId') {
			if (fieldValue.trim() && !isEmailIdValid(fieldValue)) {
				return false;
			}
		}

		return true;
	};

	const handleItemNumberChange = (itemIndex, itemNumber) => {
		setItems(items =>
			items.map((item, index) => {
				if (index === itemIndex) {
					const updatedItem = { ...item, itemNumber: itemNumber.toUpperCase() };

					if (![item.itemNumber, item.originalItemNumber].includes(itemNumber.toUpperCase())) {
						updatedItem.itemDetails = null;
						updatedItem.originalItemNumber = null;
						updatedItem.itemDescription = '';
						delete updatedItem.isNLA;
						delete updatedItem.isRestricted;
						delete updatedItem.isInvalidSupersedure;
						delete updatedItem.isVolumeDiscountEligible;
						delete updatedItem.isInvalid;
						delete updatedItem.isUnsourced;
						delete updatedItem.isNotPriced;
					}
					return updatedItem;
				}
				return item;
			})
		);
	};

	const handleOrderDetailsChange = e => {
		const fieldName = e.target.name;
		const fieldValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

		setFields(fields => {
			const newFields = { ...fields, [fieldName]: fieldValue };

			if (fieldName === 'orderType') {
				newFields.shippingMethod = getShippingMethods(fieldValue)[0].displayName;
			}

			return newFields;
		});

		if (errors[fieldName] && isFieldValid(fieldName, fieldValue)) {
			setErrors(errors => ({ ...errors, [fieldName]: false }));
		}
	};

	const handleBlur = e => {
		const { name: fieldName, value: fieldValue } = e.target;
		let isValid;
		if (fieldName === 'phoneNumber') {
			const phoneNumber = props.normalizePhoneNumber(fieldValue);
			isValid = isFieldValid(fieldName, phoneNumber);
			setFields(fields => ({
				...fields,
				phoneNumber
			}));
		} else {
			isValid = isFieldValid(fieldName, fieldValue);
		}

		if (errors[fieldName] === isValid) {
			setErrors(errors => ({
				...errors,
				[fieldName]: !isValid
			}));
		}
	};

	const handleRequestedQuantityChange = (itemIndex, value) => {
		let modifiedItem;

		setItems(
			items.map((item, index) => {
				return index === itemIndex ? (modifiedItem = { ...item, requestedQuantity: value, requestedQuantityError: value <= 0 }) : item;
			})
		);

		if (modifiedItem.itemNumber && !modifiedItem.isInvalid && !modifiedItem.isNLA && !modifiedItem.isRestricted && !modifiedItem.isUnsourced && modifiedItem.itemDetails) {
			const { method, input } = changeMethodRef.current[itemIndex];

			method({
				target: {
					value: input.value
				},
				onChangeOfQuantity: true
			});
		}
	};

	const handleCustomerReferenceChange = (itemIndex, value) => {
		setItems(items => items.map((item, index) => (index === itemIndex ? { ...item, customerReference: value } : item)));
	};

	const updateItems = (itemIndex, data) => {
		let updatedItems = [...items];

		if (Array.isArray(data)) {
			if (!data.length) {
				updatedItems = updatedItems.map((item, index) => (index === itemIndex ? { ...item, isInvalid: true } : item));
			} else {
				const availabilityPricingOfImportedFile = JSON.parse(JSON.stringify(order.availabilityPricingOfImportedFile)) || [];
				data.forEach(item => {
					availabilityPricingOfImportedFile.push({
						id: item.id,
						itemNumber: item.itemNumber,
						agreementNumber: item.agreementNumber,
						availabilityData: item.availabilityData ? [item.availabilityData] : [],
						pricingData: {
							items: item.pricingData ? [item.pricingData] : []
						}
					});
				});

				dispatch(saveAvailabilityPricingOfImportedFile(availabilityPricingOfImportedFile));

				const itemToUpdate = updatedItems[itemIndex];

				updatedItems.splice(
					itemIndex,
					1,
					...data.map(item => {
						const { pimData, availabilityData, orderabilityData, pricingData, ...rest } = item;
						return {
							...itemToUpdate,
							...rest,
							itemDetails: { pimData },
							isNewItem: true
						};
					})
				);
			}

			setItems(updatedItems);
		} else {
			setItems(items =>
				items.map((item, index) => {
					if (index === itemIndex) {
						return {
							...item,
							requestedQuantity: item.requestedQuantity || 1,
							isNewItem: false,
							isAddressChanged: false,
							isAvailabilityChanged: false,
							isPriceChanged: false,
							isUnsourced: data.availabilityData && ([57, 80].includes(data.availabilityData.errorCode) || !data.availabilityData.sourceOrganizationID),
							itemDetails: item.itemDetails ? { ...item.itemDetails, ...data } : { ...data }
						};
					}
					return item;
				})
			);
		}
	};

	const handleDialog = (title, e) => {
		e && e.preventDefault();

		switch (title) {
			case 'addressSearchDialog':
				setDialog({
					id: 'addressSearchDialog',
					onConfirm: handleAddressSelection,
					onClose: () => setDialog(null)
				});
				break;
			case 'addAttentionToDialog':
				setDialog({
					id: 'addAttentionToDialog',
					onClose: () => setDialog(null)
				});
				break;
			case 'newAddressDialog':
				setDialog({
					id: 'newAddressDialog',
					onClose: () => setDialog(null)
				});
				break;
			case 'noItemsDialog':
				setDialog({
					id: 'noItemsDialog',
					title: 'No Items',
					content: 'Please add at least one item to proceed with your order.',
					buttons: 'ok',
					onClose: () => setDialog(null)
				});
				break;
			case 'invalidItemsDialog':
				setDialog({
					id: 'invalidItemsDialog',
					title: 'Invalid Items',
					content: 'Your order has invalid items, Please remove and resubmit.',
					buttons: 'ok',
					onClose: () => setDialog(null)
				});
				break;
			default:
				break;
		}
	};

	const handleSearchByStoreNumberOrLocation = e => {
		e.preventDefault();
		handleDialog('addressSearchDialog');
	};

	const getShippingMethod = () => getShippingMethods(fields.orderType).find(shippingMethodItem => shippingMethodItem.displayName === fields.shippingMethod);

	const isFormValid = () => {
		let isEmptyList = true;
		for (const item of items) {
			if (item.itemNumber && (!item.itemDetails || item.isRestricted || item.isNLA || item.isNotPriced || item.isUnsourced)) {
				handleDialog('invalidItemsDialog');
				return false;
			}
			if (isEmptyList && item.itemNumber.trim()) {
				isEmptyList = false;
			}
		}

		if (isEmptyList) {
			handleDialog('noItemsDialog');
			return false;
		}

		for (const fieldName in fields) {
			const isValid = isFieldValid(fieldName, fields[fieldName]);

			if (!isValid && !errors[fieldName]) {
				setErrors(errors => ({
					...errors,
					[fieldName]: true
				}));
			}

			if (!isValid) {
				return false;
			}
		}

		return true;
	};

	const createNewConsumerAddress = callback => {
		if (fields.shipTo.LOCATION === 'NEW_SHIP_TO_NCRP') {
			callback(fields.shipTo);
		} else {
			const account = user.genericNCRPAccount.accountDetails;

			const payload = {
				firstName: fields.shipTo.NAME,
				lastName: ' ',
				accountId: account.ACCOUNTID,
				orgName: account.ORGANIZATIONNAME,
				accountPartyId: account.PARTYID,
				customerClassification: account.CUSTOMERCLASSCODE,
				address1: fields.shipTo.ADDRESS1.trim(),
				address2: fields.shipTo.ADDRESS2 ? fields.shipTo.ADDRESS2.trim() : '',
				address3: fields.shipTo.ADDRESS3 ? fields.shipTo.ADDRESS3.trim() : '',
				city: fields.shipTo.CITY,
				state: fields.shipTo.STATE || '',
				province: fields.shipTo.PROVINCE,
				zip: fields.shipTo.POSTALCODE,
				postalCode: fields.shipTo.POSTALCODE,
				country: fields.shipTo.COUNTRY,
				county: fields.shipTo.COUNTY
			};

			dispatch(
				personShipTo(payload, shipToResponse => {
					if (shipToResponse.status && shipToResponse.responseObject && shipToResponse.responseObject.siteUseId) {
						callback({
							...fields.shipTo,
							SITE_USE_ID: shipToResponse.responseObject.siteUseId,
							SHIP_TO_ACCOUNT_ID: shipToResponse.responseObject.custAccountId,
							LOCATION: 'NEW_SHIP_TO_NCRP'
						});
					} else {
						setErrorMessage('There was an error creating ship to, please try again.');
					}
				})
			);
		}
	};

	const handleOrderCreation = e => {
		e.preventDefault();

		if (!isFormValid() || !salesAgreement['Account Reference']) {
			return;
		}

		const newItems = JSON.parse(JSON.stringify(items.filter(item => item.itemNumber.trim())));

		const subAccountClassification = salesAgreement['Sub Account Classification'];

		// For now, hardcoding sold to and ship to sitenumber to 119752335 (account number - 116153688) for CA_CUST_CODE for online pricing
		const pricingPayload = {
			CA_CUST_CODE: `${salesAgreement.AGREEMENT_NUMBER}|${ncrpData.accountNumber}|119752335|${ncrpData.accountNumber}|119752335`,
			CA_ZIP_CODE: ['US', 'CA'].includes(fields.shipTo.COUNTRY) ? props.customZipCode : '00000',
			CA_ORDER_TYPE: fields.orderType,
			CA_CUST_CLASS: subAccountClassification && subAccountClassification.slice(-3),
			CA_RDD: moment().format('YYYYMMDD'),
			CA_SHP_COMP_CODE: `${fields.orderType}Z-${getShippingMethod().code}`,
			CA_ACTION_FLAG: 'D',
			CA_SOURCE: 'NCRP'
		};

		pricingPayload.InputCollection = [];

		props.getItemDetails(
			{ items: newItems.map(item => ({ id: item.id, itemNumber: item.itemNumber, requestedQuantity: item.requestedQuantity, itemType: item.itemType })) },
			data => {
				const availabilityOnChangeAddress = [];
				let isAvailabilityIssue = false;

				newItems.forEach(item => {
					const foundItem = data.items.find(updatedItem => updatedItem.id === item.id && updatedItem.itemNumber === item.itemNumber);
					availabilityOnChangeAddress.push({
						id: item.id,
						sku: foundItem.itemNumber,
						availabilityData: foundItem.availabilityData ? [foundItem.availabilityData] : []
					});
					item.itemDetails.availabilityData = foundItem.availabilityData;
					item.isAvailabilityChanged = true;
					if (foundItem.availabilityData && ([57, 80].includes(foundItem.availabilityData.errorCode) || !foundItem.availabilityData.sourceOrganizationID)) {
						isAvailabilityIssue = true;
					}

					if (item.itemDetails.availabilityData) {
						item.tempIndex = pricingPayload.InputCollection.length + 1;
						pricingPayload.InputCollection.push({
							CA_LINE_NO: item.tempIndex,
							CA_PART_NUMBER: item.itemNumber,
							CA_QUANTITY: item.requestedQuantity,
							CA_AVAILABLE_QTY: item.itemDetails.availabilityData.availableQuantity,
							CA_WHSE: item.itemDetails.availabilityData.sourceOrganizationCode && item.itemDetails.availabilityData.sourceOrganizationCode.split(':').at(-1),
							CA_ETA_DATE:
								item.itemDetails.availabilityData.errorCode === 53
									? item.itemDetails.availabilityData.shipDate
										? moment(item.itemDetails.availabilityData.shipDate).format('YYYYMMDD')
										: undefined
									: undefined,
							CA_UNIT_PRICE: '0'
							// CA_NET: 'N'
						});
					}
				});

				dispatch(saveAvailabilityOnChangeAddress(availabilityOnChangeAddress));
				setItems(newItems);

				if (!isAvailabilityIssue) {
					if (pricingPayload.InputCollection.length) {
						dispatch(
							getOnlinePricingForParts(pricingPayload, `requestType=priceOrder&${PNET_APP_QUERY_PARAM}`, onlinePricingResponse => {
								let isPricingIssue = false;

								newItems.forEach(item => {
									if (item.tempIndex) {
										const onlinePricingData =
											onlinePricingResponse.responseObject &&
											onlinePricingResponse.responseObject.OutputCollection &&
											onlinePricingResponse.responseObject.OutputCollection.find(
												pricingItem => pricingItem.CA_PART_NUMBER === item.itemNumber && pricingItem.CA_LINE_NO === item.tempIndex.toString()
											);

										if (!onlinePricingData || onlinePricingData.EXTENDED_PRICE === null) {
											item.isNotPriced = true;
											isPricingIssue = true;
										}
										delete item.tempIndex;
									}
								});

								if (!isPricingIssue) {
									// Creating consumer shipto if it is customer's ship to
									createNewConsumerAddress(shipTo => {
										const orderCreationPayload = {
											P_OPERATION: 'CREATE',
											P_OPERATING_UNIT: 'GE US Appliances',
											RESPONSIBILITY: 'GEOM_SETUP _US_APL',
											RESP_APPLICATION: 'GEONT',
											SECURITY_GROUP: 'STANDARD',
											NLS_LANGUAGE: 'AMERICAN',
											P_HEADER_REC: {
												OPERATION: 'CREATE',
												ATTRIBUTE2: user.userDetails.sso,
												ATTRIBUTE8: fields.phoneNumber.replaceAll(/[^0-9]/g, ''),
												ATTRIBUTE14: salesAgreement['Account Reference'],
												ATTRIBUTE15: 'CONCESSION IN WARRANTY',
												BOOKED_FLAG: shipTo.COUNTRY === 'US' ? 'Y' : 'N',
												REQUEST_DATE: moment().format('YYYY-MM-DDT00:00:00'),
												CUST_PO_NUMBER: fields.customerPONumber,
												SHIPPING_INSTRUCTIONS: fields.shippingInstructions.trim(),
												BLANKET_NUMBER: salesAgreement.AGREEMENT_NUMBER.toString(),
												ORG_ID: 82,
												SHIP_FROM_ORG_ID: '202', // default to 202 for JEF
												SHIP_TO_ORG_ID: shipTo.SITE_USE_ID && shipTo.SITE_USE_ID.toString()
											},
											P_HEADER_VAL_REC: {
												CUSTOMER_NUMBER: ncrpData.accountNumber,
												ORDER_TYPE: 'Standard Sales',
												ORDER_SOURCE: 'NCRP',
												// PRICE_LIST: selectedPriceSource.PRICE_LIST_NAME,
												SHIPPING_METHOD: getShippingMethod().ebsShipMethodName,
												SHIPMENT_PRIORITY: shippingPriorities[fields.orderType + (fields.shipSet === '1' ? 'T' : 'Z')]
											},
											P_LINE_TBL: {
												P_LINE_TBL_ITEM: newItems.map((item, index) => ({
													OPERATION: 'CREATE',
													ORDERED_QUANTITY: +item.requestedQuantity,
													LINE_NUMBER: (index + 1).toString(),
													ORDER_QUANTITY_UOM:
														item.itemDetails.pimData.primary_unit_of_measure === 'EACH' ? 'EA' : item.itemDetails.pimData.primary_unit_of_measure,
													ORG_ID: 82,
													USER_ITEM_DESCRIPTION: item.itemDetails.pimData.item_description,
													SHIP_SET_ID: '1',
													SHIP_FROM_ORG_ID:
														item.itemDetails.availabilityData.sourceOrganizationID && item.itemDetails.availabilityData.sourceOrganizationID.toString(),
													ATTRIBUTE9: 'TRUE',
													ATTRIBUTE16: item.originalItemNumber || undefined,
													ATTRIBUTE17: item.originalItemNumber ? 'Superseded' : undefined,
													ATTRIBUTE4: (item.customerReference || '').trim()
													// CALCULATE_PICE_FLAG: "Y"
												}))
											},
											P_LINE_VAL_TBL: {
												P_LINE_VAL_TBL_ITEM: newItems.map(item => ({
													INVENTORY_ITEM: item.itemNumber,
													SHIPPING_METHOD: item.itemType === 'ACCESSORY' ? 'FedEx Ground' : getShippingMethod().ebsShipMethodName,
													LINE_TYPE: 'Ship Only'
												}))
											},
											P_OLD_LINE_VAL_TBL: {
												P_LINE_VAL_TBL_ITEM: [
													{
														INVOICE_TO_ADDRESS1: ncrpData.invoiceToAddress.ADDRESS1,
														INVOICE_TO_CITY: ncrpData.invoiceToAddress.CITY,
														INVOICE_TO_ZIP: ncrpData.invoiceToAddress.POSTALCODE,
														INVOICE_TO_STATE: ncrpData.invoiceToAddress.STATE,
														INVOICE_TO_COUNTRY: ncrpData.invoiceToAddress.COUNTRY
													}
												]
											},
											SEND_EMAIL_CONFIRMATION_TO_INPUTTER: fields.emailConfirmationToInputter
										};

										orderCreationPayload.UDA_PAYLOAD = {
											apiVersion: '1.0',
											objectName: 'OE_ORDER_HEADERS_ALL',
											rowTable: {
												rowTableTime: [
													{
														rowIdentifier: 9993,
														groupId: 1249,
														groupappId: 660,
														groupType: 'OE_HEADER_ATTRIBUTES_EXT',
														groupName: 'PARTS_MANUAL',
														dataLevel: 'ORDER_HEADER'
													}
												]
											},
											dataTable: {
												dataTableItem: [
													{
														rowIdentifier: 9993,
														name: 'Consol_Pick',
														dispValue: 'Y'
													},
													{
														rowIdentifier: 9993,
														name: 'Customer_Instructions',
														dispValue: fields.customerInstructions.trim()
													},
													{
														rowIdentifier: 9993,
														name: 'Print_Price',
														dispValue: 'N'
													},
													{
														rowIdentifier: 9993,
														name: 'EMail',
														dispValue: fields.emailId.trim()
													}
												]
											},
											colValuePairs: {
												colValuePairsItem: [
													{
														name: 'HEADER_ID'
													}
												]
											},
											clsValuePairs: {
												clsValuePairsItem: [
													{
														name: 'ENTITY',
														value: 'ADMIN_DEFINED'
													}
												]
											}
										};

										if (user.userDetails.isNonGEASsoUser && user.userDetails.isManRepSsoUser) {
											orderCreationPayload.SALES_REP_NUM = props.getASMAndDSASMSSOList();
										}

										// console.log('ORDER CREATION PAYLOAD: ', orderCreationPayload);

										dispatch(
											processPartsOrder(orderCreationPayload, 'requestType=createOrder&appName=NCRP', true, orderCreationResponse => {
												if (orderCreationResponse.status && orderCreationResponse.responseObject) {
													dispatch(
														saveCreateOrderPayload({
															items: newItems,
															orderDetails: orderCreationResponse.responseObject,
															shipTo,
															billTo: { ...ncrpData.invoiceToAddress, NAME: user.genericNCRPAccount.accountDetails.ORGANIZATIONNAME }
														})
													);
													browserHistory.push('/ncrp-order/confirmation');
												}
											})
										);
									});
								} else {
									setItems(newItems);
									handleDialog('invalidItemsDialog');
								}
							})
						);
					}
				} else {
					handleDialog('invalidItemsDialog');
				}
			},
			{
				searchContext: 'NCRP',
				include: { availability: true },
				showLoader: true
			}
		);
	};

	const isJobCodeMissing = salesAgreement && !salesAgreement['Account Reference'];

	return (
		<React.Fragment>
			<section className="ncrpOrder">
				<div className="ncrpOrderHeader smallPush">
					<h1 className="pageTitle">No Charge Replacement Parts</h1>
				</div>
				<div className="flex justify-end">
					{errorMessage && (
						<p className="bold red" style={{ flexGrow: 1 }}>
							{errorMessage}
						</p>
					)}
					{isJobCodeMissing && (
						<p className="bold red" style={{ flexGrow: 1 }}>
							No Job Code found for this sales agreement, orders can't be placed.
						</p>
					)}
					<p className="bold text-right">
						<a href="https://www.ordergeapplianceparts.com/" target="_blank">
							Click here to search for parts
						</a>
					</p>
				</div>
				<CollapsibleSection title="Order Details">
					<div className="flex justify-between">
						<div>
							<div>
								<div className="ncrpOrderInfoItem">
									<b>Ship To</b>
									<div>
										<p className="my-0">
											{fields.shipTo.NAME && (
												<React.Fragment>
													{fields.shipTo.NAME}
													<br />
												</React.Fragment>
											)}
											{fields.shipTo.ADDRESS1}
											<br />
											{fields.shipTo.ADDRESS2 && (
												<React.Fragment>
													{fields.shipTo.ADDRESS2}
													<br />
												</React.Fragment>
											)}
											{fields.shipTo.ADDRESS3 && (
												<React.Fragment>
													{fields.shipTo.ADDRESS3}
													<br />
												</React.Fragment>
											)}
											{fields.shipTo.CITY && <React.Fragment>{fields.shipTo.CITY}, </React.Fragment>}
											{(fields.shipTo.STATE || fields.shipTo.PROVINCE) && <React.Fragment>{fields.shipTo.STATE || fields.shipTo.PROVINCE}, </React.Fragment>}
											{fields.shipTo.COUNTRY && <React.Fragment>{fields.shipTo.COUNTRY}, </React.Fragment>}
											{fields.shipTo.POSTALCODE && <React.Fragment>{fields.shipTo.POSTALCODE}</React.Fragment>}
										</p>
										<p className="mb-0">
											<a href="" onClick={handleSearchByStoreNumberOrLocation}>
												Search by Store Number or Location
											</a>
											<br />
											<a href="" className="inline-block" onClick={handleDialog.bind(this, 'addAttentionToDialog')} style={{ marginTop: '.5rem' }}>
												Add Attention To
											</a>
											<br />
											<a href="" className="inline-block" onClick={handleDialog.bind(this, 'newAddressDialog')} style={{ marginTop: '.5rem' }}>
												Create New
											</a>
										</p>
									</div>
								</div>
							</div>
							<div className="ncrpOrderInfoItem">
								<label htmlFor="ncrpOrderType">
									<b>Order Type</b>
								</label>
								<select id="ncrpOrderType" name="orderType" value={fields.orderType} onChange={handleOrderDetailsChange}>
									<option value="R">Regular</option>
									<option value="E">Emergency</option>
								</select>
							</div>
							<div className="ncrpOrderInfoItem">
								<label htmlFor="ncrpShippingMethod">
									<b>Ship Method</b>
								</label>
								<select id="ncrpShippingMethod" name="shippingMethod" value={fields.shippingMethod} onChange={handleOrderDetailsChange}>
									{getShippingMethods(fields.orderType).map(shippingMethod => (
										<option key={shippingMethod.displayName} value={shippingMethod.displayName}>
											{shippingMethod.displayName}
										</option>
									))}
								</select>
							</div>
						</div>
						<div>
							<div className={`ncrpOrderInfoItem${errors.emailId ? ' error' : ''}`}>
								<label htmlFor="ncrpEmailId">
									<b>Email Address</b>
								</label>
								<div>
									<input
										id="ncrpEmailId"
										name="emailId"
										className="fullWidth"
										type="text"
										value={fields.emailId}
										onChange={handleOrderDetailsChange}
										onBlur={handleBlur}
									/>
									{errors.emailId && <p className="message">Please enter a valid email Id</p>}
								</div>
							</div>
							<div className="ncrpOrderInfoItem">
								<label htmlFor="ncrpEmailConfirmationToInputter">
									<b>Email confirmation to Inputter?</b>
								</label>
								<div>
									<input
										id="ncrpEmailConfirmationToInputter"
										name="emailConfirmationToInputter"
										type="checkbox"
										value={fields.emailConfirmationToInputter}
										onChange={handleOrderDetailsChange}
									/>
								</div>
							</div>
							<div className={`ncrpOrderInfoItem${errors.phoneNumber ? ' error' : ''}`}>
								<label htmlFor="ncrpPhoneNumber">
									<b>
										Phone Number <span className="required">*</span>
									</b>
								</label>
								<div>
									<input
										id="ncrpPhoneNumber"
										name="phoneNumber"
										className="fullWidth"
										type="text"
										value={fields.phoneNumber}
										onChange={handleOrderDetailsChange}
										onBlur={handleBlur}
									/>
									{errors.phoneNumber && <p className="message">Please enter a valid phone number</p>}
								</div>
							</div>
							<div className="ncrpOrderInfoItem">
								<b>Customer PO Number</b>
								<div>{fields.customerPONumber}</div>
							</div>
						</div>
					</div>
				</CollapsibleSection>
				<CollapsibleSection title="Instructions">
					<div className="flex justify-between">
						<div>
							<div className="ncrpOrderInfoItem">
								<label htmlFor="ncrpCustomerInstructions">
									<b>Customer Instructions</b>
								</label>
								<textarea
									id="ncrpCustomerInstructions"
									name="customerInstructions"
									rows={4}
									maxLength={150}
									value={fields.customerInstructions}
									onChange={handleOrderDetailsChange}
								/>
							</div>
						</div>
						<div>
							<div className="ncrpOrderInfoItem">
								<label htmlFor="ncrpShippingInstructions">
									<b>Shipping Instructions</b>
								</label>
								<textarea
									id="ncrpShippingInstructions"
									name="shippingInstructions"
									rows={4}
									maxLength={150}
									value={fields.shippingInstructions}
									onChange={handleOrderDetailsChange}
								/>
							</div>
						</div>
					</div>
				</CollapsibleSection>
				<CollapsibleSection title="Part Information">
					<form className="ncrpOrderForm">
						<table className="ncrpOrderTable dataTable font12 fullWidth mt-0">
							<thead>
								<tr>
									<th className="textCenter">
										<a className="deleteAllLink" href="" onClick={handleRemoveAll}>
											✗
										</a>
									</th>
									<th className="textCenter" style={{ paddingInline: 0 }}>
										Item
									</th>
									<th className="textCenter">Original Item</th>
									<th className="textCenter" style={{ width: '15rem' }}>
										Item Description
									</th>
									<th className="textCenter">Requested Qty</th>
									<th className="textCenter">Qty Available</th>
									<th className="textCenter">Whse</th>
									<th className="textCenter" style={{ width: '6rem' }}>
										ETA
									</th>
									<th className="textCenter" style={{ width: '8rem' }}>
										Customer Reference
									</th>
								</tr>
							</thead>
							<tbody className="text-center">
								{items.map((item, index) => {
									const itemDetails = item.itemDetails;
									const itemPimData = (itemDetails && itemDetails.pimData) || {};
									// commenting below code, may add it back if needed
									// const isShipDateAfterRequestedDate =
									// 	itemDetails &&
									// 	itemDetails.availabilityData &&
									// 	itemDetails.availabilityData.errorCode === 53 &&
									// 	itemDetails.availabilityData.shipDate &&
									// 	moment(itemDetails.availabilityData.shipDate, 'DD-MMM-YY').isAfter(moment(), 'day');
									const isQuantityNotAvailableOnRequestedDate =
										itemDetails && itemDetails.availabilityData && itemDetails.availabilityData.availableQuantity >= 1410065408;
									const itemAvailabilityData =
										itemDetails && itemDetails.availabilityData
											? {
													availableQuantity: isQuantityNotAvailableOnRequestedDate ? 0 : itemDetails.availabilityData.availableQuantity,
													warehouseCode:
														itemDetails.availabilityData.sourceOrganizationCode &&
														itemDetails.availabilityData.sourceOrganizationCode.split(':').at(-1),
													eta: itemDetails.availabilityData.arrivalDate,
													availabilityMessage: isQuantityNotAvailableOnRequestedDate ? itemDetails.availabilityData.arrivalDate && 'BACKORDER' : '',
													errorCode: itemDetails.availabilityData.errorCode
											  }
											: {};
									// const itemPricingData = itemDetails && itemDetails.pricingData && itemDetails.pricingData.records && itemDetails.pricingData.records[0];
									return (
										<tr key={item.id}>
											<td>
												<a className="deleteLink" href="" onClick={handleRemoveLine.bind(this, index)}>
													✗
												</a>
											</td>
											<td>
												<AccountNumberAutoSuggests
													checkAvailabilityInBatches={props.checkAvailabilityInBatches}
													value={item.itemNumber}
													setChangeMethod={(method, input) => (changeMethodRef.current[index] = { method, input })}
													updateQuickOrderState={updateItems}
													onChangeProductNumberTextBox={handleItemNumberChange}
													itemIndex={index}
													zipCode={props.customZipCode}
													isNewItem={item.isNewItem}
													isAddressChanged={item.isAddressChanged || item.isAvailabilityChanged}
													isPriceChanged={item.isPriceChanged}
													width="6rem"
													whichPage="quickorder"
													itemDetails={item}
													requestedShipDate={moment()}
													// quantity={item.requestedQuantity}
													searchContext="NCRP"
													getItemDetails={props.getItemDetails}
												/>
											</td>
											<td>{item.originalItemNumber}</td>
											<td
												className={
													item.isInvalid ||
													// (itemDetails && (!itemPricingData || itemPricingData.unitPrice === null)) ||
													(itemDetails && item.isNotPriced) ||
													item.isNLA ||
													item.isRestricted ||
													item.isUnsourced
														? 'red'
														: undefined
												}
											>
												{(item.isRestricted && 'Restricted') ||
													(item.isNLA && 'No Longer Available') ||
													(item.isInvalid && 'Invalid Item') ||
													// (itemDetails && (!itemPricingData || itemPricingData.unitPrice === null) && 'Item not in Price List') ||
													(itemDetails && item.isNotPriced && 'Item not in Price List') ||
													(item.isUnsourced && 'This item cannot be ordered from this warehouse') ||
													itemPimData.item_description}
											</td>
											<td className={`pNetItemReqQty${item.requestedQuantityError ? ' pNetItemReqQtyError' : ''}`}>
												<QuantityInput
													// className={requestedQuantityError && 'error'}
													value={item.requestedQuantity}
													onChange={handleRequestedQuantityChange.bind(this, index)}
													disabled={
														item.isInvalid || item.isRestricted || item.isNLA || item.isUnsourced || (item.itemNumber && !item.itemDetails)
														// (item.itemNumber && (!itemPricingData || itemPricingData.unitPrice === null))
													}
												/>
											</td>
											<td className={isQuantityNotAvailableOnRequestedDate && !itemAvailabilityData.eta ? ' bold red' : ''}>
												<React.Fragment>
													{itemAvailabilityData.availableQuantity}
													{itemAvailabilityData.availabilityMessage && (
														<strong className="block" style={{ fontSize: '.5rem', color: '#7a7a7a' }}>
															{itemAvailabilityData.availabilityMessage}
														</strong>
													)}
												</React.Fragment>
											</td>
											<td>{itemAvailabilityData.warehouseCode}</td>
											<td>{isQuantityNotAvailableOnRequestedDate && itemAvailabilityData.eta && moment(itemAvailabilityData.eta).format('MM/DD/YYYY')}</td>
											<td>
												<CustomerReferenceInput value={item.customerReference} onChange={handleCustomerReferenceChange.bind(this, index)} />
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						<div className="flex justify-between">
							<button type="button" className="px-0" onClick={handleMoreLines} style={{ background: 'none', color: '#1a63a2', fontSize: 'inherit' }}>
								+ <u>ADD more lines</u>
							</button>
							{hasCreateOrderAccess && hasPricingAccess && (
								<button id="createOrderSubmit" type="button" className="blueButton" onClick={handleOrderCreation} disabled={isJobCodeMissing}>
									Book Order
								</button>
							)}
						</div>
					</form>
				</CollapsibleSection>
			</section>
			{dialog && dialog.id === 'addressSearchDialog' && (
				<AddressSearchDialog getCombinedAddresses={props.getCombinedAddresses} onConfirm={dialog.onConfirm} onClose={dialog.onClose} />
			)}
			{dialog && dialog.id === 'addAttentionToDialog' && (
				<NewShipToAddressOverlayDialog
					showDialog={true}
					context="NCRP"
					address={fields.shipTo}
					skipAddressValidation
					disabledFields={DISABLED_ADDRESS_FIELDS}
					saveCustomZipCode={props.saveCustomZipCode}
					onClose={dialog.onClose}
				/>
			)}
			{dialog && dialog.id === 'newAddressDialog' && (
				<NewShipToAddressOverlayDialog
					showDialog={true}
					context="NCRP"
					address={{ NAME: fields.shipTo.NAME }}
					saveCustomZipCode={props.saveCustomZipCode}
					onClose={dialog.onClose}
				/>
			)}
			{dialog && (dialog.id === 'invalidItemsDialog' || dialog.id === 'noItemsDialog') && (
				<GenericOverlayDialog
					showDialog={true}
					buttons={dialog.buttons}
					genericDialogHeading={dialog.title}
					genericDialogText={dialog.content}
					flag={dialog.id}
					handleOk={dialog.onClose}
					defaultTop
				/>
			)}
		</React.Fragment>
	);
};

export default NCRPOrderForm;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/Order/ncrpOrderForm.jsx